/**
 * Minimal WHIP (publish) and WHEP (play) clients against MediaMTX.
 *
 * Non-trickle on purpose: we wait for ICE gathering to finish and post one
 * complete SDP. On a LAN that costs a few hundred milliseconds at setup and
 * removes a whole class of failure from a prototype that has to work first
 * time, in a gym, on someone else's phone.
 */

async function waitForIce(pc: RTCPeerConnection, timeoutMs = 3000): Promise<void> {
  if (pc.iceGatheringState === 'complete') return;
  await new Promise<void>((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check);
      clearTimeout(timer);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === 'complete') done();
    };
    const timer = setTimeout(done, timeoutMs);
    pc.addEventListener('icegatheringstatechange', check);
  });
}

/**
 * A refusal from the media plane, carrying the STATUS as a value.
 *
 * **Issue 019b: a refusal drawn as a dark picture is an instrument failure
 * rendered as the subject's fault.** A monitor that receives 401 drew the same
 * black rectangle as a camera sending no pixels, so "refused" and "dark" were
 * indistinguishable by looking, and an operator reported the monitor as broken
 * three times in one session while it was correct every time. The credential
 * fix removed that cause and not the class: the status has to arrive somewhere
 * a tile can render it.
 *
 * **The URL is REDACTED, and that is not tidiness.** A WHEP url carries
 * `MEDIA_OPERATOR_TOKEN` in its query string, so the old message put a
 * credential into every console line and into anything that logged the error.
 * The path is what identifies the feed and the query is what must not travel.
 */
export class MediaExchangeError extends Error {
  constructor(
    url: string,
    readonly status: number,
    statusText: string,
  ) {
    let where = url;
    try { const u = new URL(url, 'http://x'); where = u.pathname; } catch { where = '(unparsed url)'; }
    super(`${where} responded ${String(status)} ${statusText}`);
    this.name = 'MediaExchangeError';
  }
}

async function exchange(pc: RTCPeerConnection, url: string): Promise<void> {
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await waitForIce(pc);

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/sdp' },
    body: pc.localDescription!.sdp,
  });

  if (!res.ok) {
    throw new MediaExchangeError(url, res.status, res.statusText);
  }

  await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
}

/**
 * Publish H264, never VP8.
 *
 * MediaMTX carries VP8 over WebRTC and drops it everywhere else. Its fMP4
 * recorder writes an audio-only segment (`skipping track 2 (VP8)`) and its HLS
 * muxer skips the track too. Both halves matter here: P1 samples the
 * RECORDINGS, so a VP8 feed offers the picture gate no frames at all and sits
 * on `awaiting picture review` for as long as it publishes, and the public
 * output would carry sound and no picture even if such a feed reached air.
 *
 * Chrome offers VP8 first, so the browser default is the broken one, and
 * nothing in the failure says "codec": the publisher is told
 * `ffmpeg exited 234: Failed to set value '0:v' for option 'map'`, which is
 * ffmpeg reporting that the segment it was handed has no video stream in it.
 *
 * Measured 2026-08-29 against this MediaMTX: with H264 first the answer keeps
 * H264 as the selected payload, the path reports `tracks=['Opus', 'H264']`,
 * and the recorded segment probes as `h264,video`.
 */
function preferH264(pc: RTCPeerConnection): void {
  const caps = RTCRtpSender.getCapabilities?.('video');
  if (!caps) return;
  const isH264 = (c: { mimeType: string }) => /h264/i.test(c.mimeType);
  const h264 = caps.codecs.filter(isH264);
  // Leave the offer alone rather than send an empty preference list. A browser
  // with no H264 is one this cannot help, and an empty list reads as "no
  // codecs" and drops the video m-line, which is a worse failure than VP8:
  // VP8 at least reaches the director's monitor.
  if (h264.length === 0) return;
  const ordered = [...h264, ...caps.codecs.filter((c) => !isH264(c))];
  for (const t of pc.getTransceivers()) {
    if (t.sender.track?.kind !== 'video') continue;
    t.setCodecPreferences?.(ordered);
  }
}

export async function publish(url: string, stream: MediaStream): Promise<RTCPeerConnection> {
  const pc = new RTCPeerConnection({ iceServers: [] });
  for (const track of stream.getTracks()) pc.addTrack(track, stream);
  preferH264(pc);
  await exchange(pc, url);
  return pc;
}

export async function play(url: string, video: HTMLVideoElement): Promise<RTCPeerConnection> {
  const pc = new RTCPeerConnection({ iceServers: [] });
  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });

  const inbound = new MediaStream();
  pc.addEventListener('track', (ev) => {
    inbound.addTrack(ev.track);
    video.srcObject = inbound;
  });

  await exchange(pc, url);
  return pc;
}

export function whipUrl(base: string, path: string): string {
  return `${base.replace(/\/$/, '')}/${path}/whip`;
}

export function whepUrl(base: string, path: string): string {
  return `${base.replace(/\/$/, '')}/${path}/whep`;
}
